import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import {
  attachPlayoffGame,
  createPlayoffSeriesGame,
  deletePlayoffBracket,
  deletePlayoffSeriesGame,
  detachPlayoffGame,
  fetchLeaguePlayers,
  fetchPlayoffEditor,
  fetchPlayoffGameCandidates,
  fetchPlayoffSeedSuggestions,
  fetchSeasons,
  savePlayoffBracket,
  savePlayoffMatchupNotes,
  savePlayoffResult,
  updatePlayoffSeriesGame,
  type PlayoffBracketPayload,
  type PlayoffSeriesGamePayload,
} from '../services/league'
import { PlayoffBracket } from './PlayoffBracket'
import { Card, EmptyState, Loading, Notice, Segmented, formatDate } from './ui'
import { TeamHelmet } from './TeamHelmet'
import { TeamSelect } from './TeamSelect'
import type {
  LeaguePlayer,
  PlayoffEntrantRegion,
  PlayoffFormat,
  PlayoffPairing,
  PlayoffGameCandidate,
  PlayoffMatchup,
  PlayoffSeedSuggestion,
  PlayoffSeriesGame,
  PlayoffSeriesSide,
} from '../types'
import './PlayoffsPanel.css'

/**
 * The admin editor for a season's postseason.
 *
 * @remarks
 * Ordered the way the job is done: pick a season, choose the format, whether
 * later rounds reseed, and series length, fill the field (auto-seeding it from
 * the standings if the season has any), save, then click through the drawn
 * bracket recording results. The preview is the same {@link PlayoffBracket} the
 * public page renders, so nothing an admin approves here can look different
 * once it is live.
 *
 * A finished title series writes the Hall of Champions (and therefore player
 * history) on its own. Where a later hand-edit of the Hall disagrees with the
 * bracket, that is reported here.
 */
export default function PlayoffsPanel() {
  const seasons = useAsync(fetchSeasons, [])
  const players = useAsync(fetchLeaguePlayers, [])
  const [seasonNumber, setSeasonNumber] = useState<number | null>(null)

  useEffect(() => {
    if (seasonNumber === null && seasons.data && seasons.data.length > 0) {
      setSeasonNumber(seasons.data.find(season => season.isCurrent)?.number ?? seasons.data[0].number)
    }
  }, [seasons.data, seasonNumber])

  if (seasons.loading || players.loading) {
    return (
      <Card title="Playoffs">
        <Loading rows={4} />
      </Card>
    )
  }

  return (
    <div className="stack">
      <Card
        title="Playoffs"
        subtitle="Build a season's bracket, seed it, and record the series as they finish."
        actions={
          <select
            className="select"
            style={{ minWidth: 180 }}
            value={seasonNumber ?? ''}
            onChange={event => setSeasonNumber(Number(event.target.value))}
          >
            {(seasons.data ?? []).map(season => (
              <option key={season.id} value={season.number}>
                Season {season.number}
                {season.isCurrent ? ' (current)' : ''}
              </option>
            ))}
          </select>
        }
      >
        <p className="muted ppl-hint">
          Brackets are entered by hand. Auto-seeding fills the field from the regular-season
          standings; every seed stays editable afterwards. Choose whether later rounds stay on
          the opening draw or reseed after each round when you create the bracket.
        </p>
      </Card>

      {seasonNumber !== null && (
        <SeasonPlayoffEditor
          key={seasonNumber}
          seasonNumber={seasonNumber}
          players={players.data ?? []}
        />
      )}
    </div>
  )
}

/** One row of the seed list an admin is building. */
interface EntrantDraft {
  region: PlayoffEntrantRegion
  leaguePlayerId: number
  teamAbbreviation: string | null
}

function SeasonPlayoffEditor({
  seasonNumber,
  players,
}: {
  seasonNumber: number
  players: LeaguePlayer[]
}) {
  const editor = useAsync(
    useCallback(() => fetchPlayoffEditor(seasonNumber), [seasonNumber]),
    [seasonNumber]
  )
  const seeding = useAsync(
    useCallback(() => fetchPlayoffSeedSuggestions(seasonNumber), [seasonNumber]),
    [seasonNumber]
  )

  const [format, setFormat] = useState<PlayoffFormat>('conference')
  const [pairing, setPairing] = useState<PlayoffPairing>('reseed')
  const [seriesBestOf, setSeriesBestOf] = useState<1 | 3>(3)
  const [notes, setNotes] = useState('')
  const [drafts, setDrafts] = useState<EntrantDraft[]>([])
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [openMatchupId, setOpenMatchupId] = useState<number | null>(null)

  // Reloading replaces the draft only while the admin has no unsaved edits, so
  // a refresh triggered by recording a result cannot discard a half-built field.
  useEffect(() => {
    const bracket = editor.data?.bracket
    if (!bracket || dirty) return

    setFormat(bracket.format)
    setPairing(bracket.pairing)
    setSeriesBestOf(bracket.seriesBestOf === 1 ? 1 : 3)
    setNotes(bracket.notes ?? '')
    setDrafts(
      bracket.regions.flatMap(region =>
        region.entrants.map(entrant => ({
          region: region.region,
          leaguePlayerId: entrant.leaguePlayerId,
          teamAbbreviation: entrant.teamAbbreviation,
        }))
      )
    )
  }, [editor.data, dirty])

  const nameOf = useMemo(() => {
    const index = new Map(players.map(player => [player.id, player.displayName]))
    return (id: number) => index.get(id) ?? `Player ${id}`
  }, [players])

  const regions: PlayoffEntrantRegion[] = format === 'unified' ? ['OPEN'] : ['AFC', 'NFC']

  const update = (next: EntrantDraft[]) => {
    setDrafts(next)
    setDirty(true)
    setMessage(null)
  }

  const applySuggestion = (region: PlayoffEntrantRegion, suggestions: PlayoffSeedSuggestion[]) => {
    update([
      ...drafts.filter(draft => draft.region !== region),
      ...suggestions.map(row => ({
        region,
        leaguePlayerId: row.leaguePlayerId,
        teamAbbreviation: null,
      })),
    ])
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const payload: PlayoffBracketPayload = {
        format,
        pairing,
        seriesBestOf,
        notes: notes.trim() || null,
        entrants: regions.flatMap(region =>
          drafts
            .filter(draft => draft.region === region)
            .map((draft, index) => ({
              region,
              seed: index + 1,
              leaguePlayerId: draft.leaguePlayerId,
              teamAbbreviation: draft.teamAbbreviation,
            }))
        ),
      }

      await savePlayoffBracket(seasonNumber, payload)
      setDirty(false)
      setMessage('Bracket saved.')
      editor.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await deletePlayoffBracket(seasonNumber)
      setDrafts([])
      setDirty(false)
      setMessage('Bracket deleted.')
      editor.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  if (editor.loading) {
    return (
      <Card>
        <Loading rows={5} />
      </Card>
    )
  }

  const bracket = editor.data?.bracket ?? null
  const advisory = editor.data?.championAdvisory
  const openMatchup = bracket ? findMatchup(bracket, openMatchupId) : null

  const suggestionsFor = (region: PlayoffEntrantRegion): PlayoffSeedSuggestion[] => {
    if (!seeding.data) return []
    if (region === 'AFC') return seeding.data.afc
    if (region === 'NFC') return seeding.data.nfc
    return seeding.data.unified
  }

  return (
    <div className="stack">
      {error && <Notice tone="error">{error}</Notice>}
      {message && <Notice tone="success">{message}</Notice>}

      {advisory && advisory.differences.length > 0 && (
        <Notice tone="info">
          <div>
            <strong>Bracket and Hall of Champions disagree.</strong>
            <ul className="ppl-advisory">
              {advisory.differences.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <span>
              A finished bracket writes the Hall of Champions. This notice means the Hall was later
              edited to disagree.
            </span>
          </div>
        </Notice>
      )}

      <Card
        title="Field and format"
        subtitle="Saving rebuilds the draw from these seeds. A reseeded bracket redraws after each round; results survive wherever the same two players still meet."
        actions={
          <div className="row">
            <button
              type="button"
              className="btn btn--accent btn--sm"
              onClick={save}
              disabled={busy || drafts.length < 2}
            >
              {bracket ? 'Save bracket' : 'Create bracket'}
            </button>
            {bracket && (
              <button type="button" className="btn btn--sm" onClick={remove} disabled={busy}>
                Delete
              </button>
            )}
          </div>
        }
      >
        <div className="stack-sm">
          <div className="row">
            <div className="field">
              <span className="field__label">Format</span>
              <Segmented
                options={[
                  { value: 'conference', label: 'AFC / NFC' },
                  { value: 'unified', label: 'Single bracket' },
                ]}
                value={format}
                onChange={next => {
                  const nextFormat = next as PlayoffFormat
                  setFormat(nextFormat)
                  setDirty(true)
                  // Region membership is part of the field, so a format change
                  // has to move everybody rather than leave orphaned seeds.
                  setDrafts(current =>
                    current.map(draft => ({
                      ...draft,
                      region: nextFormat === 'unified' ? 'OPEN' : draft.region === 'OPEN' ? 'AFC' : draft.region,
                    }))
                  )
                }}
              />
            </div>

            <div className="field">
              <span className="field__label">Later rounds</span>
              <Segmented
                options={[
                  { value: 'reseed', label: 'Reseed each round' },
                  { value: 'fixed', label: 'Fixed bracket' },
                ]}
                value={pairing}
                onChange={next => {
                  setPairing(next as PlayoffPairing)
                  setDirty(true)
                }}
              />
              <span className="field__hint">
                {pairing === 'reseed'
                  ? 'After each round, highest remaining seed plays lowest remaining.'
                  : '1 plays 8, and that winner meets the 4/5 winner, for the whole bracket.'}
              </span>
            </div>

            <div className="field">
              <span className="field__label">Series length</span>
              <Segmented
                options={[
                  { value: '1', label: 'Single game' },
                  { value: '3', label: 'Best of 3' },
                ]}
                value={String(seriesBestOf)}
                onChange={next => {
                  setSeriesBestOf(next === '1' ? 1 : 3)
                  setDirty(true)
                }}
              />
              <span className="field__hint">
                One setting for every round. The bracket and the result form both read it.
              </span>
            </div>
          </div>

          <label className="field">
            <span className="field__label">Notes</span>
            <input
              className="input"
              value={notes}
              onChange={event => {
                setNotes(event.target.value)
                setDirty(true)
              }}
              placeholder="Anything worth saying about this postseason"
            />
          </label>

          <div className="ppl-regions">
            {regions.map(region => (
              <SeedList
                key={region}
                region={region}
                label={region === 'OPEN' ? 'Playoff field' : region}
                drafts={drafts.filter(draft => draft.region === region)}
                allDrafts={drafts}
                players={players}
                nameOf={nameOf}
                suggestions={suggestionsFor(region)}
                onChange={update}
                onAutoSeed={count =>
                  applySuggestion(region, suggestionsFor(region).slice(0, count))
                }
              />
            ))}
          </div>

          {dirty && <Notice tone="info">Unsaved changes. The preview below still shows the saved bracket.</Notice>}
        </div>
      </Card>

      {bracket ? (
        <Card title="Bracket" subtitle="Click a series to record its result." flush>
          <div className="ppl-preview">
            <PlayoffBracket
              bracket={bracket}
              linkPlayers={false}
              selectedMatchupId={openMatchupId}
              onSelectMatchup={matchup => setOpenMatchupId(matchup.id)}
            />
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState title="No bracket yet">
            Seed the field above and save to draw it.
          </EmptyState>
        </Card>
      )}

      {openMatchup && (
        <SeriesEditor
          matchup={openMatchup}
          seriesBestOf={bracket!.seriesBestOf}
          onClose={() => setOpenMatchupId(null)}
          onSaved={() => editor.reload()}
        />
      )}
    </div>
  )
}

function findMatchup(
  bracket: NonNullable<Awaited<ReturnType<typeof fetchPlayoffEditor>>['bracket']>,
  id: number | null
): PlayoffMatchup | null {
  if (id === null) return null
  if (bracket.championship?.id === id) return bracket.championship

  for (const region of bracket.regions) {
    for (const round of region.rounds) {
      for (const cell of round.cells) {
        if (cell.kind === 'matchup' && cell.matchup.id === id) return cell.matchup
      }
    }
  }
  return null
}

/**
 * One region's ordered seed list.
 *
 * @remarks
 * Order *is* the seeding: position 1 is the 1 seed. Reordering rather than
 * typing seed numbers removes the whole class of mistakes where two players
 * hold the same seed or a field of six skips from 4 to 6, which the server
 * would refuse anyway.
 */
function SeedList({
  region,
  label,
  drafts,
  allDrafts,
  players,
  nameOf,
  suggestions,
  onChange,
  onAutoSeed,
}: {
  region: PlayoffEntrantRegion
  label: string
  drafts: EntrantDraft[]
  allDrafts: EntrantDraft[]
  players: LeaguePlayer[]
  nameOf: (id: number) => string
  suggestions: PlayoffSeedSuggestion[]
  onChange: (next: EntrantDraft[]) => void
  onAutoSeed: (count: number) => void
}) {
  const [adding, setAdding] = useState('')

  const taken = new Set(allDrafts.map(draft => draft.leaguePlayerId))
  const others = allDrafts.filter(draft => draft.region !== region)

  const replace = (next: EntrantDraft[]) => onChange([...others, ...next])

  const move = (index: number, delta: number) => {
    const next = [...drafts]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    replace(next)
  }

  const suggestionCounts = [4, 6, 8].filter(count => count <= suggestions.length)

  return (
    <section className="ppl-region">
      <header className="ppl-region__head">
        <h4>{label}</h4>
        <div className="row">
          {suggestionCounts.map(count => (
            <button
              key={count}
              type="button"
              className="btn btn--sm"
              onClick={() => onAutoSeed(count)}
              title="Seeds from the regular-season standings, tiebreakers included"
            >
              Auto-seed top {count}
            </button>
          ))}
          {suggestions.length > 0 && suggestions.length < 4 && (
            <button type="button" className="btn btn--sm" onClick={() => onAutoSeed(suggestions.length)}>
              Auto-seed all {suggestions.length}
            </button>
          )}
        </div>
      </header>

      {drafts.length === 0 ? (
        <p className="muted ppl-hint">Nobody seeded yet.</p>
      ) : (
        <ol className="ppl-seeds">
          {drafts.map((draft, index) => (
            <li key={draft.leaguePlayerId} className="ppl-seed">
              <span className="ppl-seed__number">{index + 1}</span>
              <TeamHelmet abbreviation={draft.teamAbbreviation} size="sm" />
              <span className="ppl-seed__name">{nameOf(draft.leaguePlayerId)}</span>

              <TeamSelect
                className="ppl-seed__team"
                labels="abbr"
                value={draft.teamAbbreviation ?? ''}
                onChange={next => {
                  const nextDrafts = [...drafts]
                  nextDrafts[index] = { ...draft, teamAbbreviation: next || null }
                  replace(nextDrafts)
                }}
                aria-label="Tecmo team"
              />

              <button
                type="button"
                className="btn btn--sm ppl-seed__move"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${nameOf(draft.leaguePlayerId)} up`}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn--sm ppl-seed__move"
                onClick={() => move(index, 1)}
                disabled={index === drafts.length - 1}
                aria-label={`Move ${nameOf(draft.leaguePlayerId)} down`}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn btn--sm ppl-seed__move"
                onClick={() => replace(drafts.filter((_, position) => position !== index))}
                aria-label={`Remove ${nameOf(draft.leaguePlayerId)}`}
              >
                ×
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="row">
        <select
          className="select"
          style={{ maxWidth: 220 }}
          value={adding}
          onChange={event => setAdding(event.target.value)}
        >
          <option value="">Add a player…</option>
          {players
            .filter(player => !taken.has(player.id))
            .map(player => (
              <option key={player.id} value={player.id}>
                {player.displayName}
              </option>
            ))}
        </select>
        <button
          type="button"
          className="btn btn--sm"
          disabled={!adding}
          onClick={() => {
            replace([...drafts, { region, leaguePlayerId: Number(adding), teamAbbreviation: null }])
            setAdding('')
          }}
        >
          Add
        </button>
      </div>
    </section>
  )
}

/**
 * The result form for one series.
 *
 * @remarks
 * Two ways to record a series, and the form makes plain which one is in force.
 * Entering games is the fuller path: each game is a real `games` row, and the
 * series score is then counted off them. Typing a bare `2-1` is the path for
 * the seasons whose game-by-game detail nobody wrote down. The two are never
 * both live — the first game recorded takes over, and the typed fields go away
 * rather than sitting there disagreeing.
 */
function SeriesEditor({
  matchup,
  seriesBestOf,
  onClose,
  onSaved,
}: {
  matchup: PlayoffMatchup
  seriesBestOf: number
  onClose: () => void
  onSaved: () => void
}) {
  const [topWins, setTopWins] = useState(matchup.top.wins)
  const [bottomWins, setBottomWins] = useState(matchup.bottom.wins)
  const [playedOn, setPlayedOn] = useState(matchup.playedOn ?? '')
  const [notes, setNotes] = useState(matchup.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const candidates = useAsync(
    useCallback(() => fetchPlayoffGameCandidates(matchup.id), [matchup.id]),
    [matchup.id]
  )

  useEffect(() => {
    setTopWins(matchup.top.wins)
    setBottomWins(matchup.bottom.wins)
    setPlayedOn(matchup.playedOn ?? '')
    setNotes(matchup.notes ?? '')
  }, [matchup])

  const winsNeeded = Math.ceil(seriesBestOf / 2)
  const ready = matchup.top.entrant !== null && matchup.bottom.entrant !== null

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      onSaved()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  const winnerEntrantId =
    topWins >= winsNeeded
      ? matchup.top.entrant?.id ?? null
      : bottomWins >= winsNeeded
        ? matchup.bottom.entrant?.id ?? null
        : null

  return (
    <Card
      title={matchup.label}
      subtitle={
        ready
          ? `${matchup.top.entrant!.displayName} vs ${matchup.bottom.entrant!.displayName}`
          : 'Both sides are decided by earlier rounds.'
      }
      actions={
        <button type="button" className="btn btn--sm" onClick={onClose}>
          Close
        </button>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}

      {!ready ? (
        <EmptyState title="Not ready yet">
          Record the rounds that feed this series first.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          <Notice tone="info">
            {matchup.scoreFromGames ? (
              <span>
                <strong>
                  Score from games — {matchup.top.wins}–{matchup.bottom.wins}.
                </strong>{' '}
                Counted from the {matchup.games.length}{' '}
                {matchup.games.length === 1 ? 'game' : 'games'} below. Remove them all to type a
                bare series score instead.
              </span>
            ) : (
              <span>
                <strong>Typed score.</strong> No games recorded, so the series score is whatever is
                entered here. Record a game below and the score starts counting itself.
              </span>
            )}
          </Notice>

          {!matchup.scoreFromGames && (
            <>
              <div className="row ppl-score">
                <label className="field">
                  <span className="field__label">{matchup.top.entrant!.displayName}</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={winsNeeded}
                    value={topWins}
                    onChange={event => setTopWins(Number(event.target.value))}
                  />
                </label>
                <label className="field">
                  <span className="field__label">{matchup.bottom.entrant!.displayName}</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={winsNeeded}
                    value={bottomWins}
                    onChange={event => setBottomWins(Number(event.target.value))}
                  />
                </label>
                <label className="field">
                  <span className="field__label">Played</span>
                  <input
                    className="input"
                    type="date"
                    value={playedOn}
                    onChange={event => setPlayedOn(event.target.value)}
                  />
                </label>
              </div>

              <label className="field">
                <span className="field__label">Notes</span>
                <input
                  className="input"
                  value={notes}
                  onChange={event => setNotes(event.target.value)}
                />
              </label>

              <div className="row">
                <button
                  type="button"
                  className="btn btn--accent btn--sm"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      savePlayoffResult(matchup.id, {
                        topWins,
                        bottomWins,
                        winnerEntrantId,
                        playedOn: playedOn || null,
                        notes: notes.trim() || null,
                      })
                    )
                  }
                >
                  Save series score
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      savePlayoffResult(matchup.id, {
                        topWins: 0,
                        bottomWins: 0,
                        winnerEntrantId: null,
                        playedOn: null,
                        notes: null,
                      })
                    )
                  }
                >
                  Clear
                </button>
                <span className="muted ppl-hint">
                  {winnerEntrantId === null
                    ? `First to ${winsNeeded} takes the series.`
                    : 'Winner advances on save.'}
                </span>
              </div>
            </>
          )}

          {matchup.scoreFromGames && (
            <>
              <label className="field">
                <span className="field__label">Notes</span>
                <input
                  className="input"
                  value={notes}
                  onChange={event => setNotes(event.target.value)}
                />
              </label>
              <div className="row">
                <button
                  type="button"
                  className="btn btn--accent btn--sm"
                  disabled={busy}
                  onClick={() =>
                    run(() => savePlayoffMatchupNotes(matchup.id, notes.trim() || null))
                  }
                >
                  Save notes
                </button>
              </div>
            </>
          )}

          <SeriesGames
            matchup={matchup}
            seriesBestOf={seriesBestOf}
            candidates={candidates.loading ? null : candidates.data ?? []}
            busy={busy}
            run={run}
          />
        </div>
      )}
    </Card>
  )
}

/**
 * The games inside one series: what is recorded, and the slot to add the next.
 *
 * @remarks
 * Presented as the series' slots rather than as a list, so a best-of-three
 * always shows three positions and it is obvious at a glance which are filled.
 * Save-state games sit in those same slots — a game that arrived as an upload
 * *is* one of the series games, not something running alongside them — and an
 * occupied slot has no add form, which is what stops an admin retyping a game
 * the league already has.
 */
function SeriesGames({
  matchup,
  seriesBestOf,
  candidates,
  busy,
  run,
}: {
  matchup: PlayoffMatchup
  seriesBestOf: number
  candidates: PlayoffGameCandidate[] | null
  busy: boolean
  run: (action: () => Promise<unknown>) => Promise<void>
}) {
  const [editing, setEditing] = useState<number | null>(null)
  const [adding, setAdding] = useState<number | null>(null)

  const byNumber = new Map(matchup.games.map(game => [game.gameNumber, game]))
  const slots = Array.from({ length: seriesBestOf }, (_, index) => index + 1)

  const unlinked = (candidates ?? []).filter(
    candidate => !matchup.games.some(game => game.gameId === candidate.gameId)
  )

  // A best-of-three that went 2-0 never played a third, so the empty slot after
  // a clinched series is a leftover position rather than a game to enter.
  const winsNeeded = Math.ceil(seriesBestOf / 2)
  const decided = matchup.top.wins >= winsNeeded || matchup.bottom.wins >= winsNeeded

  const topName = matchup.top.entrant!.displayName
  const bottomName = matchup.bottom.entrant!.displayName

  return (
    <div className="ppl-games">
      <h4>Games</h4>

      <ol className="ppl-games__list">
        {slots.map(number => {
          const game = byNumber.get(number)

          if (game && editing === number) {
            return (
              <li key={number} className="ppl-games__slot ppl-games__slot--form">
                <SeriesGameForm
                  gameNumber={number}
                  topName={topName}
                  bottomName={bottomName}
                  initial={game}
                  busy={busy}
                  submitLabel="Save game"
                  onCancel={() => setEditing(null)}
                  onSubmit={async payload => {
                    await run(() => updatePlayoffSeriesGame(matchup.id, game.gameId, payload))
                    setEditing(null)
                  }}
                />
              </li>
            )
          }

          if (game) {
            const topWon = game.topScore > game.bottomScore
            return (
              <li key={number} className="ppl-games__slot">
                <span className="ppl-games__number">Game {number}</span>

                <span className="ppl-games__side">
                  <TeamHelmet abbreviation={game.topTeamAbbreviation} size="sm" />
                  <span className={topWon ? 'ppl-games__won' : 'muted'}>{game.topScore}</span>
                </span>
                <span className="muted">–</span>
                <span className="ppl-games__side">
                  <span className={topWon ? 'muted' : 'ppl-games__won'}>{game.bottomScore}</span>
                  <TeamHelmet abbreviation={game.bottomTeamAbbreviation} size="sm" />
                </span>

                <span className="muted ppl-games__p1">
                  P1 {game.p1 === 'top' ? topName : bottomName}
                </span>
                <span className="muted ppl-games__date">{formatDate(game.playedAt)}</span>
                {game.source === 'state_upload' && <span className="badge">Save state</span>}
                {game.notes && (
                  <span className="muted ppl-games__recap" title={game.notes}>
                    Recap
                  </span>
                )}

                <span className="ppl-games__actions">
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy}
                    onClick={() => setEditing(number)}
                  >
                    Edit
                  </button>
                  {game.source === 'state_upload' ? (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      title="Uploaded games are unlinked from the series, not deleted"
                      onClick={() => run(() => detachPlayoffGame(matchup.id, game.gameId))}
                    >
                      Unlink
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => run(() => deletePlayoffSeriesGame(matchup.id, game.gameId))}
                    >
                      Delete
                    </button>
                  )}
                </span>
              </li>
            )
          }

          if (adding === number) {
            return (
              <li key={number} className="ppl-games__slot ppl-games__slot--form">
                <SeriesGameForm
                  gameNumber={number}
                  topName={topName}
                  bottomName={bottomName}
                  initial={null}
                  busy={busy}
                  submitLabel="Record game"
                  onCancel={() => setAdding(null)}
                  onSubmit={async payload => {
                    await run(() => createPlayoffSeriesGame(matchup.id, payload))
                    setAdding(null)
                  }}
                />
              </li>
            )
          }

          return (
            <li key={number} className="ppl-games__slot ppl-games__slot--empty">
              <span className="ppl-games__number">Game {number}</span>
              <span className="muted">
                {decided ? 'Not played — series already decided' : 'Not recorded'}
              </span>
              <span className="ppl-games__actions">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => setAdding(number)}
                >
                  Add result
                </button>
              </span>
            </li>
          )
        })}
      </ol>

      {candidates === null ? (
        <Loading rows={1} />
      ) : unlinked.length > 0 ? (
        <div className="ppl-games__candidates">
          <p className="muted ppl-hint">
            Other games on file between these two this season. Official playoff games attach
            themselves when the series is unique; link a leftover by hand rather than retyping
            it. Check the badge first: these two also played a regular-season game, and linking
            that one would take it out of the standings.
          </p>
          <ul className="ppl-links__list">
            {unlinked.map(candidate => (
              <li key={candidate.gameId}>
                <span className="ppl-links__score">
                  {candidate.topScore}–{candidate.bottomScore}
                </span>
                <span className="muted">{formatDate(candidate.playedAt)}</span>
                <span className={`badge${candidate.isPlayoff ? ' badge--playoff' : ''}`}>
                  {candidate.isPlayoff ? 'Playoff' : 'Regular season'}
                </span>
                {candidate.linkedElsewhere && (
                  <span className="badge">Linked to another series</span>
                )}
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => run(() => attachPlayoffGame(matchup.id, candidate.gameId))}
                >
                  Link
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted ppl-hint">
          Recorded games become real game rows: they carry the playoff badge, show up in the Games
          list and in both players' head-to-head, and stay out of the regular-season standings.
        </p>
      )}
    </div>
  )
}

/**
 * One game's teams, scores, date and recap.
 *
 * @remarks
 * Laid out top side then bottom side to match the series box above it, so an
 * admin reading a bracket and an admin filling in this form are looking at the
 * same two players in the same order. The recap is the same `games.notes`
 * field the rest of the site uses, so a write-up typed here is the one on the
 * game page.
 */
function SeriesGameForm({
  gameNumber,
  topName,
  bottomName,
  initial,
  busy,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  gameNumber: number
  topName: string
  bottomName: string
  initial: PlayoffSeriesGame | null
  busy: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: (payload: PlayoffSeriesGamePayload) => Promise<void>
}) {
  const [topTeam, setTopTeam] = useState(initial?.topTeamAbbreviation ?? '')
  const [bottomTeam, setBottomTeam] = useState(initial?.bottomTeamAbbreviation ?? '')
  const [topScore, setTopScore] = useState(initial ? String(initial.topScore) : '')
  const [bottomScore, setBottomScore] = useState(initial ? String(initial.bottomScore) : '')
  const [playedOn, setPlayedOn] = useState(initial?.playedAt?.slice(0, 10) ?? '')
  const [p1, setP1] = useState<PlayoffSeriesSide>(initial?.p1 ?? 'top')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  // Scoped to the slot so the two radios pair with each other and not with the
  // form in the series box above.
  const p1Group = `ppl-p1-${gameNumber}-${initial?.gameId ?? 'new'}`

  const complete = topScore !== '' && bottomScore !== ''
  const tied = topScore !== '' && bottomScore !== '' && Number(topScore) === Number(bottomScore)

  return (
    <div className="ppl-gameform">
      <span className="ppl-games__number">Game {gameNumber}</span>

      <div className="ppl-gameform__side">
        <TeamHelmet abbreviation={topTeam || null} size="sm" />
        <span className="ppl-gameform__name">{topName}</span>
        <TeamSelect
          className="ppl-gameform__team"
          labels="abbr"
          value={topTeam}
          onChange={setTopTeam}
          aria-label={`${topName} team`}
        />
        <input
          className="input ppl-gameform__score"
          type="number"
          min={0}
          max={200}
          value={topScore}
          onChange={event => setTopScore(event.target.value)}
          aria-label={`${topName} score`}
        />
        <label className="ppl-gameform__p1">
          <input
            type="radio"
            name={p1Group}
            checked={p1 === 'top'}
            onChange={() => setP1('top')}
          />
          <span>P1</span>
        </label>
      </div>

      <div className="ppl-gameform__side">
        <TeamHelmet abbreviation={bottomTeam || null} size="sm" />
        <span className="ppl-gameform__name">{bottomName}</span>
        <TeamSelect
          className="ppl-gameform__team"
          labels="abbr"
          value={bottomTeam}
          onChange={setBottomTeam}
          aria-label={`${bottomName} team`}
        />
        <input
          className="input ppl-gameform__score"
          type="number"
          min={0}
          max={200}
          value={bottomScore}
          onChange={event => setBottomScore(event.target.value)}
          aria-label={`${bottomName} score`}
        />
        <label className="ppl-gameform__p1">
          <input
            type="radio"
            name={p1Group}
            checked={p1 === 'bottom'}
            onChange={() => setP1('bottom')}
          />
          <span>P1</span>
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span className="field__label">Played</span>
          <input
            className="input"
            type="date"
            value={playedOn}
            onChange={event => setPlayedOn(event.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Game recap or comments</span>
        <textarea
          className="input"
          rows={3}
          maxLength={2000}
          value={notes}
          placeholder="Optional — shown on the game page"
          onChange={event => setNotes(event.target.value)}
        />
      </label>

      {tied && <Notice tone="error">RTL games cannot end level — one side has to win.</Notice>}

      <div className="row">
        <button
          type="button"
          className="btn btn--accent btn--sm"
          disabled={busy || !complete || tied}
          onClick={() =>
            onSubmit({
              gameNumber,
              topTeamAbbreviation: topTeam || null,
              bottomTeamAbbreviation: bottomTeam || null,
              topScore: Number(topScore),
              bottomScore: Number(bottomScore),
              p1,
              playedOn: playedOn || null,
              notes: notes.trim() || null,
            })
          }
        >
          {submitLabel}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
